import { Component } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { colors } from "brand";
import { resolveBuildId } from "common-fe";

// #2204 - scribe-fe had no React error boundary: any uncaught render-phase
// throw (ScoringForm, ClassGrid, a provider, a lazy chunk that failed to
// load) made React 18 unmount the whole root, a blank white page with no
// in-app recovery (ReloadPrompt unmounts with it).
//
// Product decisions (kiosk doctrine, "no escape hatches", and the card's open
// questions):
//  - WHAT IT SHOWS: one plain message, one primary "Reload" button, the raw
//    error message and build id (same support-triage shape as #1919's
//    AuthErrorScreen). No other navigation, no dismiss.
//  - NO AUTO-RELOAD: a deterministic render throw would reload-loop forever
//    on a kiosk nobody is watching, and an unprompted reload under a scribe
//    mid-ride is worse than a visible button. The scribe taps Reload.
//  - NO DRAFT FLUSH: obsDrafts/totalDraft/placeDraft are React state
//    (ClassGridRow / ScoringForm), not IndexedDB. By the time this boundary
//    renders, that subtree is already unmounted and its state is gone, so
//    there is nothing left to flush. Reload is a plain location.reload(),
//    which keeps the session (sessionStorage OIDC), the outbox and the
//    scribing session (localStorage), so the scribe lands back on the app,
//    not on a login screen. It deliberately does NOT use common-fe's
//    startOver() (purges OIDC storage + service workers + caches), which is
//    for a dead auth state, not a render bug.
//  - PLACEMENT: scribe-only (wraps AppContent in App.jsx), INSIDE the
//    ThemeProvider so the fallback is themed, OUTSIDE CognitoAuthProvider so
//    a throw in any provider is caught too. ReloadPrompt stays outside it so
//    SW-update handling survives. AuthGate's fatal-auth screen (#1919) is an
//    ordinary RETURN value, not a throw, so it never reaches this boundary:
//    the two recovery surfaces cannot double up (pinned by
//    AppErrorBoundary.test.jsx).
//  - i18n-free on purpose: the failure may BE the i18n provider, so the
//    fallback must not depend on anything that can itself throw.
//  - REPORTING: console.error with the build id (the only report channel the
//    kiosk has; the visible build id is for the next bug report).
function ErrorFallback({ error }) {
  const buildId = resolveBuildId();
  return (
    <Box
      role="alert"
      data-testid="app-error-boundary"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        px: 3,
        bgcolor: "background.default",
        color: "text.primary",
        zIndex: 13000, // above MUI modals, matches AuthErrorScreen
      }}
    >
      <Typography variant="h5" sx={{ mb: 1 }}>
        Something went wrong
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, maxWidth: 480 }}>
        Scoring paused. Tap Reload to get back to your ride. After it reloads, open the
        outbox and check that everything you scored has been sent.
      </Typography>
      <Typography
        data-testid="app-error-message"
        variant="body2"
        sx={{ mb: 3, maxWidth: 480, fontFamily: "monospace", color: "text.secondary" }}
      >
        {error?.message}
      </Typography>
      <Button
        data-testid="app-error-reload"
        variant="contained"
        size="large"
        onClick={() => window.location.reload()}
        sx={{ bgcolor: colors.foxOrange, "&:hover": { bgcolor: colors.foxOrange } }}
      >
        Reload
      </Button>
      <Typography
        data-testid="app-error-build-id"
        variant="caption"
        sx={{ mt: 3, color: "text.secondary", fontFamily: "monospace" }}
      >
        Build {buildId}
      </Typography>
    </Box>
  );
}

export default class AppErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error(
      `[scribe-fe ${resolveBuildId()}] uncaught render error`,
      error,
      info?.componentStack
    );
  }

  render() {
    if (this.state.error) return <ErrorFallback error={this.state.error} />;
    return this.props.children;
  }
}
